import type { ReactElement } from 'react';
import React from 'react';
import DOMPurify from 'dompurify';
import { NavLink } from 'react-router-dom';
import { PopoverBody } from 'reactstrap';
import ControlledPopover from 'atom-components/popover';
import type { Statistics as ItemWithStats } from 'feature-components/reports/item-assessment-report';
import { getCategory } from 'feature-components/reports/item-assessment-report/columns/common-columns';
import { getCategoryLabel } from 'feature-components/reports/utils/getCategoryLabel';
import type { BaseArea } from 'services/assist-api/entities/area/areas';

/**
 * Item Tooltip
 *
 * A tooltip displaying detailed information about the item
 *
 * - category name
 * - item description
 * - difficulty (scale score)
 * - seen by
 * - correct responses
 */
export default function ItemWithStatsTooltip({
  item,
  id = `${item.id}`,
  area
}: {
  item: ItemWithStats;
  area: BaseArea;
  id?: string;
}): ReactElement {
  const itemCategory = getCategory(item.categories);
  return (
    <ControlledPopover target={(() => document.getElementById(id)) as unknown as HTMLElement}>
      <PopoverBody>
        <dl>
          <dt>Item details</dt>
          <dd>
            <table className="table table-sm">
              <thead>
                <tr>
                  <th scope="col" aria-label="Statistics" />
                  <th scope="col">Value</th>
                </tr>
              </thead>
              <tbody>
                {itemCategory && (
                  <tr>
                    <th scope="row">{getCategoryLabel(area.identifier)}</th>
                    <td>
                      {itemCategory?.alternativeName && (
                        <>
                          {itemCategory.alternativeName} <br />
                        </>
                      )}
                      {itemCategory?.name}
                    </td>
                  </tr>
                )}
                <tr>
                  <th scope="row">Description</th>
                  <td>
                    <div
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.description) }}
                    />
                  </td>
                </tr>
                <tr>
                  <th scope="row">Scale Score</th>
                  <td>{item.scaleScore}</td>
                </tr>
              </tbody>
            </table>
          </dd>
          <hr />
          <dt>Statistics</dt>
          <dd>
            <table className="table table-sm">
              <thead>
                <tr>
                  <th scope="col" aria-label="Statistics" />
                  <th scope="col">Value</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th scope="row">Encountered by</th>
                  <td className="text-end">{item.numResponses}</td>
                </tr>
                <tr>
                  <th scope="row">Number of correct responses</th>
                  <td className="text-end">{item.numResponsesCorrect}</td>
                </tr>
                <tr>
                  <th scope="row">Percentage of correct responses</th>
                  <td className="text-end">{item.percentageCorrect}%</td>
                </tr>
              </tbody>
            </table>
          </dd>
        </dl>
        <NavLink to={item.testItemLink}>Go to test item</NavLink>
      </PopoverBody>
    </ControlledPopover>
  );
}
